
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { clearToken } from './App';
import LanguageSelector from './LanguageSelector';
import { ThemeToggle } from './theme';
import { Ico } from './icons';

function initialsOf(user) {
    const src = (user?.full_name || user?.email || '?').trim();
    const parts = src.split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function Layout({ title, subtitle, actions, children }) {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);

    useEffect(() => {
        try {
            setUser(JSON.parse(localStorage.getItem('nexus_user') || 'null'));
        } catch {
            setUser(null);
        }
    }, [location.pathname]);

    useEffect(() => {
        setDrawerOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') setDrawerOpen(false);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    const handleLogout = () => {
        clearToken();
        localStorage.removeItem('nexus_user');
        navigate('/login', { replace: true });
    };

    const isSuper = user?.role === 'superadmin';

    const navItems = isSuper
        ? [
            { to: '/superadmin/tenants', label: t('nav.tenants', 'Tenants'), icon: 'building', exact: true },
        ]
        : [
            { to: '/dashboard', label: t('common.dashboard'), icon: 'home', exact: true },
            { to: '/dashboard/employees', label: t('common.employees'), icon: 'users' },
            { to: '/dashboard/reports', label: t('common.reports'), icon: 'chart' },
            { to: '/terminal-de-fichaje', label: t('nav.terminal', 'Terminal de Fichaje'), icon: 'nfc', external: true },
        ];

    const isActive = (item) => {
        if (item.exact) return location.pathname === item.to;
        return location.pathname === item.to || location.pathname.startsWith(item.to + '/');
    };

    return (
        <div className={`app-shell${drawerOpen ? ' drawer-open' : ''}`}>
            <aside className="sidebar">
                <div className="side-brand">
                    <div className="brand-mark"><Ico name="logo" size={21} strokeWidth={0} /></div>
                    <div className="brand-meta">
                        <div className="brand-name">Nexus</div>
                        <div className="brand-tag">{t('app.tagline_short', 'Time · NFC')}</div>
                    </div>
                </div>

                <div className="side-label">{t('app.workspace', 'Espacio de trabajo')}</div>
                <nav className="side-nav">
                    {navItems.map((item) => (
                        item.external ? (
                            <a
                                key={item.to}
                                href={item.to}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="nav-item"
                                title={t('nav.terminal_hint', 'Abrir en una pestaña nueva')}
                            >
                                <span className="nav-ico"><Ico name={item.icon} size={19} /></span>
                                <span>{item.label}</span>
                            </a>
                        ) : (
                            <Link
                                key={item.to}
                                to={item.to}
                                className={`nav-item${isActive(item) ? ' active' : ''}`}
                            >
                                <span className="nav-ico"><Ico name={item.icon} size={19} /></span>
                                <span>{item.label}</span>
                            </Link>
                        )
                    ))}
                </nav>

                <div className="side-foot">
                    <div className="side-user">
                        <div className="avatar">{initialsOf(user)}</div>
                        <div className="side-user-info">
                            <div className="side-user-name">
                                {user?.full_name || user?.email || t('common.user')}
                            </div>
                            <div className="side-user-mail">{user?.email || ''}</div>
                        </div>
                        <button
                            type="button"
                            className="btn-icon"
                            onClick={handleLogout}
                            title={t('common.logout')}
                            aria-label={t('common.logout')}
                        >
                            <Ico name="logout" size={16} />
                        </button>
                    </div>
                </div>
            </aside>

            <button
                type="button"
                className="drawer-backdrop"
                aria-label={t('app.close_menu', 'Cerrar menú')}
                onClick={() => setDrawerOpen(false)}
            />

            <div className="shell-main">
                <header className="topbar">
                    <div className="topbar-left">
                        <button
                            type="button"
                            className="btn-icon menu-btn"
                            onClick={() => setDrawerOpen(true)}
                            aria-label={t('app.menu', 'Menú')}
                        >
                            <Ico name="menu" size={18} />
                        </button>
                        <div className="topbar-titles">
                            <div className="topbar-title">{title}</div>
                            {subtitle && <div className="topbar-sub">{subtitle}</div>}
                        </div>
                    </div>

                    <div className="topbar-actions">
                        {actions}
                        <ThemeToggle />
                        <LanguageSelector user={user} />
                        <button type="button" className="logout-btn" onClick={handleLogout}>
                            <Ico name="logout" size={15} />
                            <span className="logout-txt">{t('common.logout')}</span>
                        </button>
                    </div>
                </header>

                <main className="content">
                    {children}
                </main>
            </div>
        </div>
    );
}

export default Layout;
